import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkboxes,
  ErrorSummary,
  InsetText,
  Tag,
} from 'nhsuk-react-components';
import ProgressBar from '@ramonak/react-progress-bar';
import Modal from '../../../components/modal/Modal.component';
import MappingTable from './MappingTable';
import {
  PATIENT_REQUIRED_FIELDS,
  buildDtoAutoMappings,
  buildPatientAutoMappings,
  mappingsToSourceToTarget,
  validateRequiredMappings,
} from './patientImportMapping';

const EMPTY_HEADERS = [];
const EMPTY_ROWS = [];
const EMPTY_MAP = {};

/**
 * Shared import mapping modal — NHS + project Modal (no antd).
 * mode: 'patient' | 'device'
 */
export default function ImportMappingModal({
  visible,
  onCancel,
  mode = 'patient',
  title,
  fileName,
  csvHeaders,
  rowCount = 0,
  sampleRows,
  sourceToTarget,
  requiredFields = PATIENT_REQUIRED_FIELDS,
  itemDto = null,
  showOverride = true,
  overrideLabel = 'Override / upsert existing records',
  defaultOverride = false,
  importing = false,
  importProgress = 0,
  importResults = null,
  importErrors = [],
  onStartImport,
}) {
  const headers = csvHeaders || EMPTY_HEADERS;
  const samples = sampleRows || EMPTY_ROWS;
  const defaultsMap = sourceToTarget || EMPTY_MAP;

  const [mappings, setMappings] = useState([]);
  const [override, setOverride] = useState(defaultOverride);
  const [validationError, setValidationError] = useState(null);

  useEffect(() => {
    if (!visible) return;
    setOverride(defaultOverride);
    setValidationError(null);
    if (mode === 'device') {
      setMappings(buildDtoAutoMappings(itemDto || {}, headers));
    } else {
      setMappings(buildPatientAutoMappings(headers, defaultsMap));
    }
  }, [visible, mode, itemDto, headers, defaultsMap, defaultOverride]);

  const mappedCount = useMemo(
    () => mappings.filter((m) => String(m.csvHeader || '').trim()).length,
    [mappings]
  );

  const sampleColumns = useMemo(() => {
    const keys = headers.length ? headers : Object.keys(samples[0] || {});
    return keys.slice(0, 6);
  }, [headers, samples]);

  const previewRows = useMemo(() => samples.slice(0, 3), [samples]);

  const handleClose = () => {
    if (importing) return;
    onCancel?.();
  };

  const handleStart = () => {
    if (mode === 'patient') {
      const missing = validateRequiredMappings(mappings, requiredFields);
      if (missing.length) {
        setValidationError(`Map required fields: ${missing.join(', ')}`);
        return;
      }
    } else {
      const barcode = mappings.find((m) => m.dtoProperty === 'barcode' || m.dtoProperty === 'Barcode');
      if (barcode && !String(barcode.csvHeader || barcode.defaultValue || '').trim()) {
        setValidationError('Map required DTO property: barcode');
        return;
      }
    }
    setValidationError(null);
    onStartImport?.({
      duplicateMode: override ? 'Override' : 'Skip',
      override,
      columnMappings: mappingsToSourceToTarget(mappings),
      mappings,
    });
  };

  const modalTitle =
    title ||
    (mode === 'device' ? 'Map device (Implant) columns' : 'Map patient & device columns');

  const progressPercent = Number.isFinite(importProgress) ? Math.max(0, Math.min(100, importProgress)) : 0;

  return (
    <Modal show={visible} handleClose={handleClose} maxWidth="960px">
      <h2 className="nhsuk-heading-m" style={{ marginTop: 0 }}>
        {modalTitle}
      </h2>

      <InsetText>
        <p style={{ marginBottom: 0 }}>
          {mode === 'device'
            ? 'DTO properties are auto-matched to file headers. Change any File column before importing.'
            : 'PatientDto / ImplantInstanceDto properties are auto-mapped from Mediconnect defaults. Change any File column before importing.'}
        </p>
      </InsetText>

      <p className="nhsuk-body-s">
        File: <strong>{fileName || '—'}</strong>
        {' · '}
        Rows: <strong>{rowCount}</strong>
        {' · '}
        Mapped: <strong>
          {mappedCount}/{mappings.length}
        </strong>
      </p>

      {showOverride && (
        <Checkboxes name="import-override" id="import-override">
          <Checkboxes.Box
            id="override-existing"
            name="override"
            value="override"
            checked={override}
            disabled={importing || Boolean(importResults)}
            onChange={(e) => setOverride(e.target.checked)}
          >
            {overrideLabel}
          </Checkboxes.Box>
        </Checkboxes>
      )}

      {validationError && (
        <ErrorSummary>
          <p>
            <strong>Error:</strong> {validationError}
          </p>
        </ErrorSummary>
      )}

      {importing && (
        <p>
          <Tag color="yellow">Import in progress — do not close this window</Tag>
        </p>
      )}

      {!importResults && (
        <MappingTable
          deviceMappings={mappings}
          setDeviceMappings={setMappings}
          csvHeaders={headers}
          requiredDtoProperties={mode === 'patient' ? requiredFields : ['barcode']}
        />
      )}

      {previewRows.length > 0 && !importResults && (
        <div style={{ marginTop: 12 }}>
          <h3 className="nhsuk-heading-xs" style={{ marginBottom: 4 }}>
            Sample rows
          </h3>
          <p className="nhsuk-body-s nhsuk-u-secondary-text-color" style={{ marginBottom: 6 }}>
            Preview only (first {previewRows.length} rows, {sampleColumns.length} columns).
          </p>
          <div
            style={{
              maxHeight: 110,
              overflow: 'auto',
              border: '1px solid #d8dde0',
              fontSize: 11,
            }}
          >
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  {sampleColumns.map((h) => (
                    <th
                      key={h}
                      style={{
                        position: 'sticky',
                        top: 0,
                        background: '#f0f4f5',
                        padding: '3px 6px',
                        textAlign: 'left',
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #d8dde0',
                        maxWidth: 100,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={h}
                    >
                      {h.length > 24 ? `${h.slice(0, 22)}…` : h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i}>
                    {sampleColumns.map((h) => (
                      <td
                        key={h}
                        style={{
                          padding: '3px 6px',
                          borderBottom: '1px solid #edf1f2',
                          maxWidth: 100,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={row[h] == null ? '' : String(row[h])}
                      >
                        {row[h] == null || row[h] === '' ? '—' : String(row[h])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {importing && (
        <div style={{ marginTop: 12 }}>
          <ProgressBar completed={progressPercent} bgColor="#005eb8" height="14px" labelSize="10px" />
          <p className="nhsuk-body-s" style={{ marginTop: 4 }}>
            {progressPercent}% complete
          </p>
        </div>
      )}

      {importResults && (
        <InsetText>
          <p>
            <strong>Import finished</strong>
          </p>
          {mode === 'device' ? (
            <p>
              Processed {importResults.processed || 0} · errors {importResults.errorCount || 0}
            </p>
          ) : (
            <>
              <p>
                Patients created {importResults.created || 0} · updated {importResults.updated || 0} ·
                skipped {importResults.skipped || 0}
              </p>
              <p>
                Devices created {importResults.devicesCreated || 0} · updated{' '}
                {importResults.devicesUpdated || 0} · skipped {importResults.devicesSkipped || 0} ·
                registry {importResults.registryCreated || 0}
              </p>
              <p>Mode: {importResults.duplicateMode || 'Skip'}</p>
              {importResults.errors?.length > 0 && (
                <ul style={{ maxHeight: 120, overflow: 'auto' }}>
                  {importResults.errors.slice(0, 30).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              )}
            </>
          )}
        </InsetText>
      )}

      {importErrors?.length > 0 && mode === 'device' && (
        <ErrorSummary>
          <p>
            <strong>Device import errors</strong>
          </p>
          <ul style={{ maxHeight: 100, overflow: 'auto' }}>
            {importErrors.slice(0, 20).map((e, i) => (
              <li key={i}>{typeof e === 'string' ? e : e.error || JSON.stringify(e)}</li>
            ))}
          </ul>
        </ErrorSummary>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button secondary onClick={handleClose} disabled={importing}>
          {importResults ? 'Close' : 'Cancel'}
        </Button>
        {!importResults && (
          <Button onClick={handleStart} disabled={!headers.length || importing}>
            {importing ? 'Importing…' : 'Start import'}
          </Button>
        )}
      </div>
    </Modal>
  );
}
